import { useSnackbar } from '@admiin-com/ds-web';
import { useMediaQuery, useTheme } from '@mui/material';
import React, { forwardRef, useCallback, useEffect, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import ESignatureDesktop from './ESignatureDesktop';
import ESignatureMobile from './ESignatureMobile';
import { checkAnnotationDirection } from '../../helpers/signature';
import { useTaskCreationContext } from '../../pages/TaskCreation';
import { TaskDirection } from '@admiin-com/ds-graphql';
import { useTranslation } from 'react-i18next';
import { SIGNATURE_COLORS } from './RecipientSignature';

export interface ESignatureProps {
  documentUrl: string;
  annotations?: any;
  userId?: string;
  onPdfLoad?: () => void;
  handleDraft?: () => Promise<void>;
  handleNext?: () => Promise<void>;
  onBack?: () => void;
}
export const DropContext = React.createContext<any>(null);

export const ESignature = forwardRef(
  (
    {
      documentUrl,
      annotations,
      userId,
      handleDraft,
      handleNext,
      onBack,
    }: ESignatureProps,
    instanceRef: any
  ) => {
    const { setTaskDirection, taskDirection, isTaxBinding, task, taskType } =
      useTaskCreationContext();
    const { setValue, control } = useFormContext();
    const { t } = useTranslation();
    const signers = useWatch({ control, name: 'signers' });
    const prevSignersRef = useRef<any[]>([]);
    const [pdfLoaded, setPdfLoaded] = React.useState(false);

    const handleInitialPagesChange = useCallback(
      async (
        signer: any,
        index: number,
        shouldAdd: boolean,
        prevValue: boolean | undefined
      ) => {
        if (!instanceRef?.current?.addInitialsToAllPages) return;
        if (shouldAdd === prevValue) return;

        const color = SIGNATURE_COLORS[index]?.backgroundColor ?? '#F7EDC4';

        if (shouldAdd) {
          await instanceRef.current.addInitialsToAllPages(signer, color);
        } else {
          await instanceRef.current.removeInitialsFromAllPages(signer.id);
        }
      },
      [instanceRef]
    );

    useEffect(() => {
      if (!pdfLoaded) return;

      const validSigners = signers?.filter(
        (signer: any) => signer.name && signer.email
      );
      const prevValidSigners = prevSignersRef.current?.filter(
        (signer: any) => signer.name && signer.email
      );

      validSigners?.forEach((signer: any, index: number) => {
        const prevSigner = prevValidSigners?.find(
          (s: any) => s.id === signer.id
        );
        const prevInitialPages = prevSigner?.initialPages;
        const currentInitialPages = signer.initialPages;

        if (currentInitialPages !== prevInitialPages) {
          handleInitialPagesChange(
            signer,
            index,
            !!currentInitialPages,
            prevInitialPages
          );
        }
      });

      prevSignersRef.current = signers ? [...signers] : [];
    }, [signers, pdfLoaded, handleInitialPagesChange]);

    const saveAnnotations = async () => {
      try {
        const allAnnotations = await Promise.all(
          Array.from({ length: instanceRef.current?.totalPageCount }).map(
            (_, pageIndex) => instanceRef.current.getAnnotations(pageIndex)
          )
        );

        const flattenedAnnotations = allAnnotations.flat();
        const instantJSON = await instanceRef.current.exportInstantJSON(
          flattenedAnnotations
        );
        const annotationsString = JSON.stringify(instantJSON);
        updateCreationContext(annotationsString);

        return annotationsString;
      } catch (e: any) {
        showSnackbar({
          message: e.message,
          severity: 'error',
          horizontal: 'right',
          vertical: 'bottom',
        });
        throw e;
      }
    };
    const [dropped, setDropped] = React.useState(false);
    const updateCreationContext = (annotationsString: string | null) => {
      setValue('annotations', annotationsString);
      let newTaskDirection = TaskDirection.SENDING;
      if (annotationsString) {
        newTaskDirection = checkAnnotationDirection(annotationsString);
      }
      if (taskType === 'tax') {
        newTaskDirection = TaskDirection.RECEIVING;
      }
      setTaskDirection(newTaskDirection);
      if (taskDirection !== newTaskDirection && !task) {
        setValue('from', null);
      }
    };
    const updateDropped = async () => {
      const allAnnotations = await Promise.all(
        Array.from({ length: instanceRef.current?.totalPageCount }).map(
          (_, pageIndex) => instanceRef.current.getAnnotations(pageIndex)
        )
      );

      const flattenedAnnotations = allAnnotations.flat();
      // console.log(flattenedAnnotations);
      const instantJSON = await instanceRef.current.exportInstantJSON(
        flattenedAnnotations
      );
      console.log(instantJSON?.annotations?.length);
      if (instantJSON?.annotations?.length > 0) {
        setDropped(true);
      } else {
        setDropped(false);
      }
    };

    const showSnackbar = useSnackbar();

    const allowSkipSignature = ['tax'].includes(taskType ?? '');

    const onSkip = allowSkipSignature
      ? async () => {
          updateCreationContext(null);
          if (handleNext) await handleNext();
        }
      : undefined;

    const onDraft = async () => {
      try {
        await saveAnnotations();
        if (handleDraft) await handleDraft();
      } catch (e: any) {
        console.log(e?.message);
      }
    };

    const onNext = async () => {
      try {
        await saveAnnotations();
        if (handleNext) await handleNext();
      } catch (e: any) {
        console.log(e?.message);
      }
    };

    const handlePdfLoad = async () => {
      setPdfLoaded(true);
      await updateDropped();
    };

    const [droppedSignature, setDroppedSignature] = React.useState<any>();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    return !isMobile ? (
      <ESignatureDesktop
        handleDraft={onDraft}
        annotations={annotations}
        userId={userId}
        onPdfLoad={handlePdfLoad}
        handleNext={onNext}
        droppedSignature={droppedSignature}
        onDropped={setDroppedSignature}
        ref={instanceRef}
        documentUrl={documentUrl}
        onAnnotationUpdated={updateDropped}
        disableNext={!dropped}
        onSkip={onSkip}
        onBack={onBack}
        isTaxBinding={isTaxBinding}
      />
    ) : (
      <ESignatureMobile
        handleDraft={onDraft}
        annotations={annotations}
        userId={userId}
        onPdfLoad={handlePdfLoad}
        handleNext={onNext}
        droppedSignature={droppedSignature}
        onDropped={setDroppedSignature}
        ref={instanceRef}
        documentUrl={documentUrl}
        onAnnotationUpdated={updateDropped}
        disableNext={!dropped}
        onSkip={onSkip}
        onBack={onBack}
      />
    );
  }
);
export default ESignature;
